import { t } from '../i18n';
import { type LocalStats, describeBest } from '../store/local';
import type { ScreenHost } from './screen';

export interface TitleInput {
  readonly stats: LocalStats;
  readonly runWordCount: number;
  /** 도전장 링크로 들어왔을 때의 상대 기록 한 줄(없으면 null) — 숫자만. 문제도 정답도 안 보인다 */
  readonly challengeLine: string | null;
  /** 버전 불일치 등 안내(없으면 null) */
  readonly challengeNote?: string | null;
  /** '그냥 내 판 하기' — 도전장을 버리고 새 판으로 */
  readonly onDecline?: (() => void) | null;
  /** 지금 팩 한 줄("팩 · 나라·도시·음식") — 도전장 화면에서는 링크가 팩을 정하므로 숨긴다 */
  readonly packLine?: string | null;
  /** 다른 팩으로 — 페이지를 다시 연다(팩은 부팅 때 한 번만 읽는다) */
  readonly onSwitchPack?: { readonly label: string; readonly go: () => void } | null;
  /** 타이틀 패널을 갈아 끼우는 부속 화면(투고 · 인기) — `back` 이 타이틀을 다시 그린다 */
  readonly extras?: readonly {
    readonly label: string;
    readonly open: (root: HTMLElement, back: () => void) => void;
  }[];
}

/**
 * 타이틀 — 첫 방문자에게도 띄운다.
 *  (1) 지금은 로드 즉시 라이브 판이라 손을 안 대면 24틱(4.3초)에 벽사가 첫 콘텐츠다.
 *  (2) iOS/Android 는 사용자 제스처 없이 AudioContext 를 못 연다 — [시작] 탭이 합법 언락 지점이다.
 * onActivate 는 버튼 click 리스너 **안에서 동기적으로** 불린다(await 뒤로 밀면 iOS 가 제스처를 잃는다).
 */
export function showTitle(
  screens: ScreenHost,
  input: TitleInput,
  onActivate: () => void,
): Promise<void> {
  return new Promise<void>((resolve) => {
    screens.open('title', (root) => {
      const build = (): void => {
        const panel = document.createElement('div');
        panel.className = 'panel title-panel';

        const h = document.createElement('h1');
        const S = t().title;
        h.textContent = t().appName;
        panel.append(h);

        if (input.challengeLine !== null) {
          const tag = document.createElement('p');
          tag.className = 'title-duel';
          tag.textContent = S.duelTag;
          panel.append(tag);
          const c = document.createElement('p');
          c.className = 'title-best';
          c.textContent = input.challengeLine;
          panel.append(c);
        }
        // 안내는 도전장 줄과 따로 그린다 — 링크의 팩을 받지 못해 도전장을 버린 경우(challengeLine null)에도 왜 새 판인지 알린다
        if (input.challengeNote != null) {
          const n = document.createElement('p');
          n.className = 'title-hint';
          n.textContent = input.challengeNote;
          panel.append(n);
        }

        const rules = document.createElement('ul');
        rules.className = 'title-rules';
        for (const line of S.rules) {
          const li = document.createElement('li');
          li.textContent = line;
          rules.append(li);
        }
        panel.append(rules);

        if (input.challengeLine === null && input.packLine != null) {
          const pk = document.createElement('p');
          pk.className = 'title-pack';
          pk.textContent = input.packLine;
          panel.append(pk);
        }

        if (input.stats.best !== null) {
          const b = document.createElement('p');
          b.className = 'title-best';
          b.textContent = S.best(
            describeBest(input.stats.best, input.runWordCount),
            input.stats.runs,
          );
          panel.append(b);
        }

        const btn = document.createElement('button');
        btn.className = 'primary';
        btn.textContent = input.challengeLine !== null ? S.duelStart : S.start;
        btn.addEventListener('click', () => {
          onActivate(); // 동기 — 오디오 언락
          screens.close('title');
          resolve();
        });
        panel.append(btn);

        if (input.challengeLine !== null && input.onDecline != null) {
          const decline = document.createElement('button');
          decline.className = 'secondary';
          decline.textContent = S.decline;
          const onDecline = input.onDecline;
          decline.addEventListener('click', () => {
            onDecline();
            onActivate();
            screens.close('title');
            resolve();
          });
          panel.append(decline);
        }

        if (input.challengeLine === null && input.onSwitchPack != null) {
          const sw = document.createElement('button');
          sw.className = 'secondary';
          sw.textContent = input.onSwitchPack.label;
          const go = input.onSwitchPack.go;
          sw.addEventListener('click', go);
          panel.append(sw);
        }

        if (input.challengeLine === null && input.extras !== undefined && input.extras.length > 0) {
          const row = document.createElement('div');
          row.className = 'title-extras';
          for (const x of input.extras) {
            const b = document.createElement('button');
            b.className = 'secondary';
            b.textContent = x.label;
            b.addEventListener('click', () => x.open(root, build));
            row.append(b);
          }
          panel.append(row);
        }

        const hint = document.createElement('p');
        hint.className = 'title-hint';
        hint.textContent = input.challengeLine !== null ? S.hintDuel : S.hintSolo;
        panel.append(hint);

        root.replaceChildren(panel);
        btn.focus();
      };
      build();
    });
  });
}
